import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, ClipboardCheck, Info, BadgeCheck, CreditCard, CheckCircle2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { buildWhatsappLinks, bookingWhatsappMessage } from "@/lib/whatsappLink";

const CUR = "\u20b9";
const fmt = (n) => (n === null || n === undefined ? "—" : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load the payment gateway"));
    document.body.appendChild(s);
  });

export default function BookingDetail() {
  const { id } = useParams();
  const { settings, customerInfo } = useApp();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payCfg, setPayCfg] = useState(null);
  const [paying, setPaying] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${id}`);
        setB(data);
      } catch (e) {
        toast.error(errMsg(e, "Could not load booking"));
      } finally { setLoading(false); }
    })();
    api.get("/payments/config").then(({ data }) => setPayCfg(data)).catch(() => setPayCfg({ enabled: false }));
  }, [id]);

  const confirmBooking = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/bookings/${id}/confirm`);
      setB((prev) => ({ ...prev, customer_confirmed: true, customer_confirmed_at: data.customer_confirmed_at }));
      toast.success("Details confirmed — you can now pay online");
    } catch (e) {
      toast.error(errMsg(e, "Could not confirm the booking details"));
    } finally {
      setConfirming(false);
    }
  };

  const payOnline = async (purposeKind) => {
    if (!b?.customer_confirmed_at) {
      toast.error("Please confirm the booking details first");
      return;
    }
    setPaying(purposeKind);
    try {
      await loadRazorpayScript();
      const { data } = await api.post("/payments/initiate", { booking_id: b.id, purpose: purposeKind });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Tezz Khana Catering",
        description: `Booking ${data.booking_reference} — ${purposeKind === "advance" ? "Advance payment" : "Full payment"}`,
        order_id: data.order_id,
        prefill: {
          name: customerInfo?.name || "",
          email: customerInfo?.email || "",
          contact: customerInfo?.mobile || "",
        },
        theme: { color: "#b45309" },
        modal: { ondismiss: () => setPaying(null) },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post("/payments/verify", {
              booking_id: b.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setB(v.booking);
            toast.success("Payment received — thank you!");
          } catch (e) {
            toast.error(errMsg(e, "Payment verification failed — please contact us on WhatsApp"));
          } finally {
            setPaying(null);
          }
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed — no amount was captured. You can try again.");
        setPaying(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e, "Could not start the payment"));
      setPaying(null);
    }
  };

  if (loading || !b) {
    return <div className="container-x py-16 text-sm text-muted-foreground">Loading…</div>;
  }

  const purpose = b.status === "payment_pending" ? "advance" : "booking";
  const links = buildWhatsappLinks(
    settings,
    bookingWhatsappMessage({ booking: b, customerName: customerInfo?.name, purpose })
  );

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="container-x py-8">
        <Link to="/account" data-testid="booking-back" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Link>

        <div className="mt-4 rounded-3xl border border-primary/30 bg-primary/5 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="kbd-eyebrow">Booking</span>
              <div className="mt-2 font-serif-display text-4xl font-semibold text-secondary" data-testid="booking-reference">
                {b.reference}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                Status: <span className="font-semibold text-secondary" data-testid="booking-status">{b.status}</span> · Payment: <span className="font-semibold text-secondary" data-testid="booking-payment-status">{b.payment_status}</span>
              </div>
              {b.advance_waived && (
                <span
                  data-testid="booking-advance-waived-badge"
                  className="mt-3 inline-flex items-center gap-1 rounded-full bg-accent/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-secondary"
                >
                  <BadgeCheck className="h-3 w-3" /> Confirmed without advance
                </span>
              )}
            </div>
            <ClipboardCheck className="h-10 w-10 text-primary" />
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <section className="surface-card">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Event</div>
              <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                <Field label="Event type" value={b.event_type} />
                <Field label="Guests" value={b.guest_count} />
                <Field label="Event date" value={b.event_date} />
                <Field label="Event time" value={b.event_time} />
                <Field label="Venue" value={b.venue} full />
              </dl>
            </section>

            {b.package_snapshot && (
              <section className="surface-card">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Package (frozen)</div>
                <div className="mt-2 font-serif-display text-xl font-semibold text-secondary">{b.package_snapshot.name}</div>
                <div className="mt-1 text-sm text-secondary/80">
                  {fmt(b.package_snapshot.price)}{b.package_snapshot.pricing_unit ? ` / ${b.package_snapshot.pricing_unit}` : ""} · min {b.package_snapshot.minimum_guests || "—"} guests
                </div>
              </section>
            )}

            {b.dishes_snapshot?.length > 0 && (
              <section className="surface-card">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Menu ({b.dishes_snapshot.length})</div>
                <ul className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
                  {b.dishes_snapshot.map((d) => (
                    <li key={d.id} className="rounded-lg border border-border/70 bg-background px-3 py-1.5">
                      <span className="font-medium text-secondary">{d.name}</span>
                      <span className="ml-1 text-[10px] uppercase tracking-[0.14em] text-accent">{d.category_name}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {b.terms_accepted_snapshot && (
              <section className="surface-card" data-testid="booking-terms-snapshot">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Terms accepted (frozen)</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  Accepted at {b.terms_accepted_at ? String(b.terms_accepted_at).slice(0, 16).replace("T", " ") : "—"}
                </div>
                <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-xl bg-muted/40 p-4 text-[11px] text-secondary/80">
                  {b.terms_accepted_snapshot}
                </pre>
              </section>
            )}
          </div>

          <aside className="surface-card h-fit lg:sticky lg:top-24">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Price</div>
            <Row label="Subtotal" value={fmt(b.pricing_snapshot?.subtotal ?? b.pricing_snapshot?.subtotal_partial)} testid="bs-subtotal" />
            {b.additional_charges_snapshot?.length > 0 && (
              <div className="mt-3 border-t border-border/60 pt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Additional charges</div>
                <ul className="mt-1 space-y-1 text-xs">
                  {b.additional_charges_snapshot.map((c, i) => (
                    <li key={i} className="flex items-center justify-between">
                      <span className="text-secondary/80">{c.label}</span>
                      <span className="font-semibold text-secondary">{fmt(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {b.gst_snapshot?.enabled && (
              <div className="mt-3 space-y-1 border-t border-border/60 pt-3 text-xs">
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground">Taxable subtotal</span>
                  <span className="font-semibold text-secondary" data-testid="bs-gst-taxable">{fmt(b.gst_snapshot.taxable_subtotal)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground">GST ({b.gst_snapshot.rate}%)</span>
                  <span className="font-semibold text-secondary" data-testid="bs-gst-amount">{fmt(b.gst_snapshot.gst_amount)}</span>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-baseline justify-between border-t border-border/70 pt-3">
              <div className="text-sm font-semibold uppercase tracking-[0.16em] text-secondary">Final amount</div>
              <div className="text-2xl font-bold text-secondary" data-testid="bs-final-amount">{fmt(b.final_amount)}</div>
            </div>
            {b.advance_requested != null && (
              <div className="mt-2 flex items-baseline justify-between text-xs">
                <span className="text-muted-foreground">Advance requested</span>
                <span className="font-semibold text-secondary" data-testid="bs-advance">{fmt(b.advance_requested)}</span>
              </div>
            )}
            {(() => {
              const paid = Number(b.amount_paid || 0);
              const canPay = payCfg?.enabled && b.status !== "cancelled" && b.payment_status !== "paid_in_full" && b.final_amount != null;
              const advDue = b.advance_requested != null && Number(b.advance_requested) > paid;
              const fullDue = Number(b.final_amount) - paid > 0;
              if (!canPay || (!advDue && !fullDue)) return null;
              return (
                <div className="mt-4 space-y-2" data-testid="booking-pay-online-section">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Pay online (secure)</div>
                  {b.customer_confirmed_at ? (
                    <div
                      data-testid="booking-confirmed-badge"
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-green-100 px-3 py-2 text-xs font-semibold text-green-700"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Confirmed on {new Date(b.customer_confirmed_at).toLocaleDateString("en-IN")}
                    </div>
                  ) : (
                    <>
                      <button
                        data-testid="booking-confirm-details-btn"
                        disabled={confirming}
                        onClick={confirmBooking}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-green-700 px-3 py-2.5 text-xs font-semibold text-white hover:bg-green-800 disabled:opacity-60"
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {confirming ? "Confirming…" : "I have reviewed & confirm these details"}
                      </button>
                      <p data-testid="booking-confirm-required-hint" className="text-[10px] font-semibold text-primary">
                        Please review the event, menu and price details, then confirm above to enable online payment.
                      </p>
                    </>
                  )}
                  {advDue && (
                    <button
                      data-testid="booking-pay-advance-btn"
                      disabled={!!paying || !b.customer_confirmed_at}
                      style={{ opacity: b.customer_confirmed_at ? 1 : 0.45, cursor: b.customer_confirmed_at ? "pointer" : "not-allowed" }}
                      onClick={() => payOnline("advance")}
                      className="btn-primary w-full !py-2.5 !text-xs"
                    >
                      <CreditCard className="h-4 w-4" />
                      {paying === "advance" ? "Opening checkout…" : `Pay advance ${fmt(Number(b.advance_requested) - paid)}`}
                    </button>
                  )}
                  {fullDue && (
                    <button
                      data-testid="booking-pay-full-btn"
                      disabled={!!paying || !b.customer_confirmed_at}
                      style={{ opacity: b.customer_confirmed_at ? 1 : 0.45, cursor: b.customer_confirmed_at ? "pointer" : "not-allowed" }}
                      onClick={() => payOnline("full")}
                      className={`${advDue ? "btn-ghost" : "btn-primary"} w-full !py-2.5 !text-xs`}
                    >
                      <CreditCard className="h-4 w-4" />
                      {paying === "full" ? "Opening checkout…" : `Pay full balance ${fmt(Number(b.final_amount) - paid)}`}
                    </button>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    Payments are processed securely by Razorpay. Your booking updates instantly after payment.
                  </p>
                </div>
              );
            })()}
            {b.status === "payment_pending" && !payCfg?.enabled && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-accent/40 bg-accent/10 p-3 text-xs text-secondary" data-testid="booking-pay-note">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Online advance payment is not enabled yet. Please arrange the advance with our team on WhatsApp using the buttons below.
              </div>
            )}
            {b.advance_waived && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 p-3 text-xs text-secondary" data-testid="booking-advance-waived-note">
                <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-primary" />
                You chose to confirm this booking without an advance. Our team will settle the advance offline as per the accepted T&amp;Cs.
              </div>
            )}
            {(b.amount_paid > 0 || (b.payments && b.payments.length > 0)) && (
              <div className="mt-4 rounded-xl border border-border/70 bg-background p-3 text-xs" data-testid="booking-payments-section">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Payments received
                </div>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-secondary">Amount paid</span>
                  <span className="font-semibold text-secondary" data-testid="booking-amount-paid">{fmt(b.amount_paid)}</span>
                </div>
                {(b.payments || []).length > 0 && (
                  <ul className="mt-2 divide-y divide-border/60">
                    {(b.payments || []).map((p, i) => (
                      <li key={p.id || i} data-testid={`booking-payment-row-${i}`} className="flex items-center justify-between py-1.5">
                        <div>
                          <div className="font-semibold text-secondary">{fmt(p.amount)}</div>
                          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                            {(p.recorded_at || "").slice(0, 10)} · {p.method || "offline"}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-[10px] text-muted-foreground">
                  {payCfg?.enabled
                    ? "Online payments reflect here instantly. Offline payments are recorded by the Tezz Khana team."
                    : "Payments are recorded by the Tezz Khana team after receiving the amount offline. Online payments are not enabled yet."}
                </p>
              </div>
            )}
            {links.length > 0 && (
              <div className="mt-4 space-y-2">
                {links.map((l, i) => (
                  <a
                    key={l.number + i}
                    data-testid={`booking-wa-${i + 1}`}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {b.status === "payment_pending" ? "Arrange advance on WhatsApp" : "Message us on WhatsApp"}
                    {links.length > 1 ? ` ${i + 1}` : ""}
                  </a>
                ))}
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium text-secondary">{value || "—"}</div>
    </div>
  );
}
function Row({ label, value, testid }) {
  return (
    <div className="mt-2 flex items-baseline justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-secondary" data-testid={testid}>{value}</span>
    </div>
  );
}
